import React from 'react'
import clsx from 'clsx'
import { ArrowLeft, ExportSquare } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useAppSelector } from '@SonarCX/hooks/redux'

import CopyableStateProvider from '@SonarCX/providers/CopyableStateProvider'

import BaseModal from '@SonarCX/components/molecules/ChatModals/BaseModal2'
import TextField from '@SonarCX/components/atoms/TextField'
import Message from '@SonarCX/components/molecules/Message'
import Tooltip from '@SonarCX/components/atoms/Tooltip'

import { selectConvo } from '@SonarCX/slices/convo'
import { selectDMs } from '@SonarCX/slices/dms'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useTimelinePagination } from '@SonarCX/providers/TimelinePaginationProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useTimelineTarget } from '@SonarCX/providers/TimelineTargetProvider'
import { useChatScroll } from '@SonarCX/providers/ChatScrollProvider'
import useStyles from './styles'

type SearchModalProps = {
  session: string
  isDirect: boolean
  onClose: () => void
}

type MessageLike = {
  id: string
  content: string
  sender: string
  senderFull: LocalUser
  tags: string[]
}

const title = 'Search messages'
const MIN_SEARCH = 2

const SearchModal: React.FC<SearchModalProps> = ({
  session,
  isDirect,
  onClose
}) => {
  const theme = useTheme()

  const { allConversations } = useAppSelector(selectConvo)
  const { dms } = useAppSelector(selectDMs)

  const { isSmall: mobile, isMedium } = useBreakpoint()
  const { getData, isDoneLoading } = useTimelinePagination()
  const {
    api: { handleFetchResult }
  } = useSocket()
  const { setTargetID } = useTimelineTarget()
  const { quickScrollToBottom } = useChatScroll()

  const [search, setSearch] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  const conversations = React.useMemo(
    () => (isDirect ? dms : allConversations),
    [isDirect, dms, allConversations]
  )

  const messages = React.useMemo(() => {
    if (!(session in conversations)) {
      return []
    }

    const msgs: MessageLike[] = conversations[session].messages

    const allMessages = msgs.map(msg => {
      const searchText = `${msg.content ?? ''} ${
        msg.sender ?? ''
      } ${msg.tags.join(' ')}`
        .toLocaleLowerCase()
        .trim()
      return {
        ...msg,
        text: searchText
      }
    })

    return allMessages
  }, [session, conversations])

  const canSearch = React.useMemo(() => {
    return search.length > MIN_SEARCH
  }, [search])

  const matches = React.useMemo(() => {
    if (!canSearch) {
      return []
    }

    const lowerSearch = search.toLocaleLowerCase()
    return messages.filter(msg => msg.text.includes(lowerSearch))
  }, [canSearch, search, messages])

  const fetchID = React.useMemo(() => {
    return conversations[session]?.owner_id ?? session
  }, [conversations, session])

  const paperProps = React.useMemo(() => {
    if (mobile) {
      //64px is nav bar
      const maxH = `calc(100% - 64px)`
      return {
        style: {
          top: '0',
          transform: 'translateX(-50%)',
          borderRadius: '0',
          width: '100vw',
          maxWidth: 'unset',
          minHeight: maxH,
          height: '100svh',
          maxHeight: maxH
        }
      }
    }

    return {
      style: {
        width: isMedium ? 'calc(100% - 4rem)' : '100%',
        maxWidth: isMedium ? 'unset' : '900px'
      }
    }
  }, [mobile, isMedium])

  const classes = useStyles()

  const handleLoadMore = React.useCallback(async () => {
    setLoading(true)
    const { data, extra } = await getData(fetchID)
    handleFetchResult(data, extra)
    setLoading(false)
  }, [getData, fetchID, handleFetchResult])

  const doneLoading = isDoneLoading(fetchID)

  const handleGotoClick = React.useCallback(
    (id: string) => {
      setTargetID(id)
      quickScrollToBottom()
      onClose()
    },
    [setTargetID, quickScrollToBottom, onClose]
  )

  return (
    <BaseModal
      open
      hideTitle={mobile}
      showX={!mobile}
      title={title}
      onClose={onClose}
      PaperProps2={paperProps}
      childrenClass={mobile && classes.mobileChildren}
    >
      <div className={clsx(classes.root, mobile && classes.mobileRoot)}>
        {mobile && (
          <div className={classes.mobileTitle}>
            <ArrowLeft
              variant='Linear'
              size={theme.size.xl}
              onClick={onClose}
              className={classes.mobileBackArrow}
            />

            <span>{title}</span>
          </div>
        )}

        <div className={classes.textField}>
          <TextField
            placeholder='Search'
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className={classes.infoMessage}>
          {doneLoading ? (
            <span>Searching through all messages.</span>
          ) : (
            <span>Searching through the last {messages.length} messages.</span>
          )}

          {!doneLoading && (
            <span
              onClick={() => {
                if (!loading) {
                  handleLoadMore()
                }
              }}
              className={loading ? 'load' : 'more'}
            >
              Load more{loading && '...'}
            </span>
          )}
        </div>

        <div id='search-container' className={classes.messageHolder}>
          {matches.length > 0 ? (
            matches.map(msg => (
              <CopyableStateProvider key={msg.id}>
                <Message
                  {...msg}
                  containerID='#search-container'
                  allowInteraction={false}
                  hoverChild={
                    <Tooltip arrow title='View in timeline'>
                      <div
                        onClick={() => handleGotoClick(msg.id)}
                        className={classes.goto}
                      >
                        <ExportSquare variant='Linear' size={theme.size.base} />
                      </div>
                    </Tooltip>
                  }
                />
              </CopyableStateProvider>
            ))
          ) : (
            <div className={classes.noResults}>
              {canSearch
                ? 'No results found.'
                : 'Enter more characters to search.'}
            </div>
          )}
        </div>
      </div>
    </BaseModal>
  )
}

export default SearchModal
